import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  pickerChevronRecipe,
  pickerMenuListRecipe,
  pickerMenuRecipe,
  pickerOptionFrameRecipe,
  pickerOptionHintRecipe,
  pickerOptionRecipe,
  pickerTriggerRecipe,
  type PickerMenuListRecipeProps,
  type PickerMenuRecipeProps,
} from '../recipes/picker.recipe';
import { useFieldContext } from './field-context';
import { Text } from './Text';

export interface PickerMenuProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
    PickerMenuRecipeProps {
  open: boolean;
  /** Called with the next state when the trigger is pressed. */
  onOpenChange: (open: boolean) => void;
  disabled?: boolean;
  /** What the closed control shows: the chosen value, or a placeholder. */
  trigger: React.ReactNode;
  /** Extra props for the trigger button — a title, a test id. */
  triggerProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
  /** How tall the open panel may grow before it scrolls. */
  maxHeight?: PickerMenuListRecipeProps['maxHeight'];
  /** The options, as `PickerMenuOption`s. Rendered only while open. */
  children: React.ReactNode;
}

/**
 * A dropdown that opens in place: a bordered trigger, and a panel of options
 * beneath it while `open`.
 *
 *   <PickerMenu
 *     open={open}
 *     onOpenChange={setOpen}
 *     trigger={<Text variant="inherit">Status: {label}</Text>}
 *   >
 *     {OPTIONS.map((o) => (
 *       <PickerMenuOption key={o.value} selected={o.value === value}
 *         onClick={() => choose(o.value)}>
 *         {o.label}
 *       </PickerMenuOption>
 *     ))}
 *   </PickerMenu>
 *
 * The screen owns the open state and decides when a choice closes it, which
 * is how the Urgently controls were written. Inside a Field the trigger
 * takes the field's id, so the label reaches it.
 */
export const PickerMenu = React.forwardRef<HTMLDivElement, PickerMenuProps>(
  function PickerMenu(
    {
      open,
      onOpenChange,
      disabled = false,
      trigger,
      triggerProps,
      maxHeight,
      width,
      className,
      children,
      ...props
    },
    ref
  ) {
    const field = useFieldContext();
    return (
      <div
        ref={ref}
        className={cn(pickerMenuRecipe({ width }), className)}
        {...props}
      >
        <button
          type="button"
          id={field?.controlId}
          aria-expanded={open}
          disabled={disabled}
          onClick={() => {
            if (!disabled) onOpenChange(!open);
          }}
          className={pickerTriggerRecipe({ open })}
          {...triggerProps}
        >
          {trigger}
          <ChevronDown
            aria-hidden="true"
            className={pickerChevronRecipe({ open })}
          />
        </button>
        {open ? (
          <div className={pickerMenuListRecipe({ maxHeight })}>{children}</div>
        ) : null}
      </div>
    );
  }
);

export interface PickerMenuOptionProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  selected?: boolean;
  /**
   * A note shown above the row on hover, for an option that needs explaining
   * before it can be chosen. Drawn only when given.
   */
  hint?: React.ReactNode;
}

/** One choice in a PickerMenu. */
export const PickerMenuOption = React.forwardRef<
  HTMLButtonElement,
  PickerMenuOptionProps
>(function PickerMenuOption(
  { selected = false, hint, className, ...props },
  ref
) {
  const option = (
    <button
      ref={ref}
      type="button"
      data-selected={selected ? '' : undefined}
      className={cn(pickerOptionRecipe({ selected }), className)}
      {...props}
    />
  );
  if (hint === undefined || hint === null || hint === false) return option;
  return (
    <div className={pickerOptionFrameRecipe()}>
      {option}
      <span role="tooltip" className={pickerOptionHintRecipe()}>
        {hint}
      </span>
    </div>
  );
});

export interface ReasonPickerOption {
  value: string;
  label: string;
}

export interface ReasonPickerProps
  extends Omit<PickerMenuProps, 'trigger' | 'children'> {
  options: ReasonPickerOption[];
  /** The chosen option's value; empty for none yet. */
  value: string;
  onValueChange: (value: string) => void;
  /** Shown, muted, while nothing is chosen. */
  placeholder?: string;
}

/**
 * A PickerMenu over a flat list of `{ value, label }` options — the cancel
 * reason, the GOA reason, and the two yes/no questions on completing a call.
 *
 *   <Field label="Cancellation reason" required>
 *     <ReasonPicker options={CANCEL_REASONS} value={reason}
 *       onValueChange={choose} open={open} onOpenChange={setOpen} />
 *   </Field>
 *
 * `onValueChange` is the whole reaction to a choice: the screen sets the
 * value and closes the menu itself, as it did before this had a name.
 */
export const ReasonPicker = React.forwardRef<HTMLDivElement, ReasonPickerProps>(
  function ReasonPicker(
    {
      options,
      value,
      onValueChange,
      placeholder = 'Select a reason...',
      ...props
    },
    ref
  ) {
    const chosen = options.find((option) => option.value === value);
    const label = value ? chosen?.label || value : placeholder;
    return (
      <PickerMenu
        ref={ref}
        trigger={
          <Text variant="inherit" tone={value ? 'default' : 'muted'}>
            {label}
          </Text>
        }
        {...props}
      >
        {options.map((option) => (
          <PickerMenuOption
            key={option.value}
            selected={value === option.value}
            onClick={() => onValueChange(option.value)}
          >
            {option.label}
          </PickerMenuOption>
        ))}
      </PickerMenu>
    );
  }
);
